import type { ImageRef, ProjectData, SceneRef } from '../../../shared/models';

/**
 * Which slugs a new scene/image must avoid (DEV-670).
 *
 * Two sources, and for a long time we only consulted one. The obvious source
 * is `project.json`'s reference graph — the siblings the user can see. The
 * other is the `scenes` / `images` collections, which can hold a document no
 * ref points at: a `PUT project.json` that failed after the `scene.json` write
 * landed, a half-applied move between chapters. Such an orphan is invisible in
 * the editor (which renders the same graph) yet still owns its row on the
 * unique `(projectSlug, chapterSlug, sceneSlug)` key, so creating an entity on
 * that slug replaces its content with no record of what was there.
 *
 * Merging the two sources turns that silent destruction into the renumber the
 * user already expects from a visible collision (`intro` → `intro-2`).
 * `OccupiedSlugs` comes from `GET /api/projects/:slug/occupied-slugs`; omitting
 * it leaves behaviour exactly as it was, which is the state before it loads.
 *
 * The taken set is built from a **ref list**, not from the project, because
 * every caller is mid-edit on its own draft of one container — a form's
 * chapter clone, a bulk handler's running set — and that draft, not the
 * last-published project, is what the new slug has to be unique against.
 *
 * Type-only imports keep this module runnable under `node --test`.
 */

/** Document slugs per chapter slug; the `''` key holds project-level documents. */
export interface OccupiedSlugs {
	scenes: Record<string, string[]>;
	images: Record<string, string[]>;
}

/** Neutral element — merging it changes nothing, which is the pre-load state. */
export const NO_OCCUPIED_SLUGS: OccupiedSlugs = { scenes: {}, images: {} };

/** The document slugs a ref resolves to — one, or `slug__0 … slug__(N-1)` when split. */
export function splitSlugsFor(ref: Pick<SceneRef, 'slug' | 'split'>): string[] {
	const split = typeof ref.split === 'number' && ref.split > 1 ? ref.split : 0;
	if (split === 0) return [ref.slug];
	return Array.from({ length: split }, (_, i) => `${ref.slug}__${i}`);
}

/** Scene refs of a container — a chapter by slug, or project level when `null`. */
export function scenesOf(project: ProjectData | undefined, chapterSlug: string | null): SceneRef[] {
	if (!project) return [];
	if (chapterSlug === null) return project.scenes ?? [];
	return (project.chapters ?? []).find((ch) => ch.slug === chapterSlug)?.scenes ?? [];
}

/** Image refs of a container — a chapter by slug, or project level when `null`. */
export function imagesOf(project: ProjectData | undefined, chapterSlug: string | null): ImageRef[] {
	if (!project) return [];
	if (chapterSlug === null) return project.images ?? [];
	return (project.chapters ?? []).find((ch) => ch.slug === chapterSlug)?.images ?? [];
}

/** Project-level documents are reported under the empty-string chapter key. */
function documentsIn(bucket: Record<string, string[]>, chapterSlug: string | null): string[] {
	return bucket[chapterSlug ?? ''] ?? [];
}

/**
 * Includes split expansions so a plain ref can't collide with an existing
 * split family's documents, plus every scene document in the same container
 * whether or not a ref points at it.
 */
export function takenSceneSlugs(
	refs: readonly Pick<SceneRef, 'slug' | 'split'>[],
	chapterSlug: string | null,
	occupied: OccupiedSlugs = NO_OCCUPIED_SLUGS,
): Set<string> {
	const taken = new Set<string>(documentsIn(occupied.scenes, chapterSlug));
	for (const ref of refs) {
		taken.add(ref.slug);
		for (const slug of splitSlugsFor(ref)) taken.add(slug);
	}
	return taken;
}

export function takenImageSlugs(
	refs: readonly Pick<ImageRef, 'slug'>[],
	chapterSlug: string | null,
	occupied: OccupiedSlugs = NO_OCCUPIED_SLUGS,
): Set<string> {
	const taken = new Set<string>(documentsIn(occupied.images, chapterSlug));
	for (const ref of refs) taken.add(ref.slug);
	return taken;
}
